import { FullPageLayout } from '../Layouts/FullPage';

export const ACTION_TYPES = {
  SET_REPORT: 'SET_REPORT',
  SET_SELECTED_PAGE: 'SET_SELECTED_PAGE',
  SET_SELECTED_LAYOUT: 'SET_SELECTED_LAYOUT',
  ADD_PAGE: 'ADD_PAGE',
  REMOVE_PAGE: 'REMOVE_PAGE',
  UPDATE_PAGE_ELEMENT: 'UPDATE_PAGE_ELEMENT',
  REMOVE_PAGE_ELEMENT: 'REMOVE_PAGE_ELEMENT',
  APPLY_LAYOUT: 'APPLY_LAYOUT',
};

export const INITIAL_STATE = {
  report: null,
  reportVersion: null,
  selectedPage: null,
  selectedLayout: null,
};

const getLatestReportVersion = versions => {
  if (!Array.isArray(versions) || versions.length === 0) return null;
  return versions.reduce((latest, current) => {
    return current?.number > (latest?.number || 0) ? current : latest;
  }, null);
};

const ensureMinimumPages = pages => {
  if (Array.isArray(pages) && pages.length > 0) return pages;

  return [
    {
      number: 1,
      layout: FullPageLayout.id,
      pageElements: [],
    },
  ];
};

const reindexPages = pages => {
  return pages.map((page, index) => ({
    ...page,
    number: index + 1,
  }));
};

export const editSmartReportReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_REPORT: {
      const { report } = action.payload;

      if (!report) {
        return INITIAL_STATE;
      }

      const latestVersion = getLatestReportVersion(report.smartReportVersions);
      const pages = ensureMinimumPages(latestVersion?.pages);
      const reportVersion = { ...latestVersion, pages };
      const selectedPage = state.selectedPage || pages[0];

      return {
        ...state,
        report,
        reportVersion,
        selectedPage,
      };
    }

    case ACTION_TYPES.SET_SELECTED_PAGE: {
      return {
        ...state,
        selectedPage: action.payload.page,
      };
    }
    case ACTION_TYPES.SET_SELECTED_LAYOUT: {
      return {
        ...state,
        selectedLayout: action.payload.layout,
      };
    }

    case ACTION_TYPES.ADD_PAGE: {
      const { layout } = action.payload;
      const currentPages = state.reportVersion?.pages || [];
      const newPage = {
        number: currentPages.length + 1,
        layout: layout?.id || FullPageLayout.id,
        pageElements: [],
      };

      const updatedPages = [...currentPages, newPage];
      const updatedReportVersion = {
        ...state.reportVersion,
        pages: updatedPages,
      };

      return {
        ...state,
        reportVersion: updatedReportVersion,
        selectedPage: newPage,
      };
    }

    case ACTION_TYPES.REMOVE_PAGE: {
      const { pageNumber } = action.payload;
      const currentPages = state.reportVersion?.pages || [];

      if (currentPages.length <= 1) {
        return state;
      }

      const filteredPages = currentPages.filter(page => page.number !== pageNumber);
      const reindexedPages = reindexPages(filteredPages);

      const updatedReportVersion = {
        ...state.reportVersion,
        pages: reindexedPages,
      };

      // Update selected page if necessary
      let newSelectedPage = state.selectedPage;
      if (state.selectedPage?.number === pageNumber) {
        const targetIndex = Math.max(0, pageNumber - 2);
        newSelectedPage = reindexedPages[targetIndex] || null;
      }

      return {
        ...state,
        reportVersion: updatedReportVersion,
        selectedPage: newSelectedPage,
      };
    }

    case ACTION_TYPES.UPDATE_PAGE_ELEMENT: {
      const { componentId, slotId, data } = action.payload;
      const { selectedPage } = state;

      if (!selectedPage) return state;

      const elements = [...(selectedPage.pageElements || [])];
      const existingIndex = elements.findIndex(el => el.slotId === slotId);

      const newElement = {
        componentId,
        slotId,
        data,
        style: {},
      };

      if (existingIndex >= 0) {
        elements[existingIndex] = newElement;
      } else {
        elements.push(newElement);
      }

      const updatedSelectedPage = {
        ...selectedPage,
        pageElements: elements,
      };

      const updatedPages = state.reportVersion.pages.map(page =>
        page.number === selectedPage.number ? updatedSelectedPage : page,
      );

      const updatedReportVersion = {
        ...state.reportVersion,
        pages: updatedPages,
      };

      return {
        ...state,
        selectedPage: updatedSelectedPage,
        reportVersion: updatedReportVersion,
      };
    }

    case ACTION_TYPES.REMOVE_PAGE_ELEMENT: {
      const { slotId } = action.payload;
      const { selectedPage } = state;

      if (!selectedPage) return state;

      const filteredElements =
        selectedPage.pageElements?.filter(element => element.slotId !== slotId) || [];

      const updatedSelectedPage = {
        ...selectedPage,
        pageElements: filteredElements,
      };

      const updatedPages = state.reportVersion.pages.map(page =>
        page.number === selectedPage.number ? updatedSelectedPage : page,
      );

      const updatedReportVersion = {
        ...state.reportVersion,
        pages: updatedPages,
      };

      return {
        ...state,
        selectedPage: updatedSelectedPage,
        reportVersion: updatedReportVersion,
      };
    }

    case ACTION_TYPES.APPLY_LAYOUT: {
      const { layout } = action.payload;
      const { selectedPage } = state;

      if (!selectedPage || !layout) return state;

      const updatedSelectedPage = {
        ...selectedPage,
        layout: layout.id,
        // clear components when applying a new layout
        pageElements: [],
      };

      const updatedPages = state.reportVersion.pages.map(page =>
        page.number === selectedPage.number ? updatedSelectedPage : page,
      );

      const updatedReportVersion = {
        ...state.reportVersion,
        pages: updatedPages,
      };

      return {
        ...state,
        selectedPage: updatedSelectedPage,
        selectedLayout: layout,
        reportVersion: updatedReportVersion,
      };
    }

    default:
      return state;
  }
};
